/**
 * Injecte / affiche les calques Martin trails_osm sur n'importe quel fond MapLibre.
 */
import { useEffect } from 'react'
import type { Map as MaplibreMap } from 'maplibre-gl'
import { useMap } from '@vis.gl/react-maplibre'
import { resolveMaplibreMap, isMapStyleReady, safeGetLayer, safeGetSource } from '../utils/mapLayerProfile'
import { isNativeApp as isNativeCapacitor } from '../../../shared/utils/platform'
import {
  buildAqpTrailHitLayerDefinitions,
  buildAqpTrailLayerDefinitions,
  buildMartinTrailHitLayerDefinitions,
  buildMartinTrailLayerDefinitions,
  emptyFeatureCollection,
  hitLayerId,
  MARTIN_TRAILS_AQP_SOURCE_ID,
  MARTIN_TRAILS_AQP_TILES_URL,
  MARTIN_TRAILS_SOURCE_ID,
  MARTIN_TRAILS_TILES_URL,
  TRAIL_HIGHLIGHT_LAYER_ID,
  TRAIL_HIGHLIGHT_SOURCE_ID,
} from '../utils/martinTrailLayers'
import {
  AQP_LAYER_MAP,
  layerIdsForTrailToggle,
  MAP_TRAIL_TYPE_IDS,
  type MapTrailTypeSelection,
} from '../utils/mapTrailTypes'

/** Sur Capacitor, ttrxyz:// passe par le cache IDB (Worker MapLibre ignore window.fetch). */
function tileTemplateForPlatform(httpsUrl: string): string {
  if (!isNativeCapacitor()) return httpsUrl
  if (httpsUrl.startsWith('ttrxyz://')) return httpsUrl
  return `ttrxyz://${httpsUrl}`
}

function ensureMartinSource(map: MaplibreMap): void {
  const osmTiles = [tileTemplateForPlatform(MARTIN_TRAILS_TILES_URL)]
  const aqpTiles = [tileTemplateForPlatform(MARTIN_TRAILS_AQP_TILES_URL)]

  if (!safeGetSource(map, MARTIN_TRAILS_SOURCE_ID)) {
    map.addSource(MARTIN_TRAILS_SOURCE_ID, {
      type: 'vector',
      tiles: osmTiles,
      minzoom: 8,
      maxzoom: 14,
    })
  } else if (isNativeCapacitor()) {
    // Style embarqué / API peut déjà avoir martin-trails en https:// → forcer ttrxyz
    const style = map.getStyle()
    const src = style?.sources?.[MARTIN_TRAILS_SOURCE_ID]
    const tiles =
      src && typeof src === 'object' && 'tiles' in src && Array.isArray(src.tiles) ? src.tiles : null
    const needsRewrite =
      Array.isArray(tiles) && tiles.some((t) => typeof t === 'string' && /^https?:\/\//i.test(t))
    if (needsRewrite) {
      for (const layer of style?.layers ?? []) {
        if ('source' in layer && layer.source === MARTIN_TRAILS_SOURCE_ID && safeGetLayer(map, layer.id)) {
          map.removeLayer(layer.id)
        }
      }
      map.removeSource(MARTIN_TRAILS_SOURCE_ID)
      map.addSource(MARTIN_TRAILS_SOURCE_ID, {
        type: 'vector',
        tiles: osmTiles,
        minzoom: 8,
        maxzoom: 14,
      })
    }
  }

  if (!safeGetSource(map, MARTIN_TRAILS_AQP_SOURCE_ID)) {
    map.addSource(MARTIN_TRAILS_AQP_SOURCE_ID, {
      type: 'vector',
      tiles: aqpTiles,
      minzoom: 6,
      maxzoom: 14,
    })
  } else if (isNativeCapacitor()) {
    const style = map.getStyle()
    const src = style?.sources?.[MARTIN_TRAILS_AQP_SOURCE_ID]
    const tiles =
      src && typeof src === 'object' && 'tiles' in src && Array.isArray(src.tiles) ? src.tiles : null
    const needsRewrite =
      Array.isArray(tiles) && tiles.some((t) => typeof t === 'string' && /^https?:\/\//i.test(t))
    if (needsRewrite) {
      for (const layer of style?.layers ?? []) {
        if (
          'source' in layer &&
          layer.source === MARTIN_TRAILS_AQP_SOURCE_ID &&
          safeGetLayer(map, layer.id)
        ) {
          map.removeLayer(layer.id)
        }
      }
      map.removeSource(MARTIN_TRAILS_AQP_SOURCE_ID)
      map.addSource(MARTIN_TRAILS_AQP_SOURCE_ID, {
        type: 'vector',
        tiles: aqpTiles,
        minzoom: 6,
        maxzoom: 14,
      })
    }
  }

  if (!safeGetSource(map, TRAIL_HIGHLIGHT_SOURCE_ID)) {
    map.addSource(TRAIL_HIGHLIGHT_SOURCE_ID, {
      type: 'geojson',
      data: emptyFeatureCollection(),
    })
  }
}

function ensureMartinLayers(map: MaplibreMap): number {
  if (!isMapStyleReady(map)) return 0
  try {
    ensureMartinSource(map)
  } catch {
    return 0
  }
  let added = 0
  // Calques visibles, puis leurs calques « hit » (zone cliquable/survolable
  // large, invisible — voir martinTrailLayers.ts) juste au-dessus.
  const defs = [
    ...buildMartinTrailLayerDefinitions(),
    ...buildAqpTrailLayerDefinitions(),
    ...buildMartinTrailHitLayerDefinitions(),
    ...buildAqpTrailHitLayerDefinitions(),
  ]
  for (const def of defs) {
    if (safeGetLayer(map, def.id)) continue
    try {
      map.addLayer(def)
      added += 1
    } catch {
      // style pas prêt
    }
  }
  // Surbrillance au survol/clic — toujours au sommet, une seule fois.
  if (!safeGetLayer(map, TRAIL_HIGHLIGHT_LAYER_ID)) {
    try {
      map.addLayer({
        id: TRAIL_HIGHLIGHT_LAYER_ID,
        type: 'line',
        source: TRAIL_HIGHLIGHT_SOURCE_ID,
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': '#ffffff',
          'line-width': 6,
          'line-opacity': 0.95,
        },
      })
      added += 1
    } catch {
      // style pas prêt
    }
  }
  return added
}

export function MartinTrailLayers({
  enabled,
  selection,
}: {
  enabled: boolean
  styleId?: string
  selection: MapTrailTypeSelection
}) {
  const { current: map } = useMap()
  const selectionKey = JSON.stringify(selection)

  useEffect(() => {
    if (!enabled || !map) return

    const native = resolveMaplibreMap(map)
    if (!native) return

    const retryTimers: ReturnType<typeof setTimeout>[] = []

    const applyVisibility = () => {
      const m = resolveMaplibreMap(map)
      if (!isMapStyleReady(m)) return 0

      ensureMartinLayers(m)

      let touched = 0
      const setLayerVisibility = (layerId: string, visible: string) => {
        if (safeGetLayer(m, layerId)) {
          m.setLayoutProperty(layerId, 'visibility', visible)
          touched += 1
        }
        const hitId = hitLayerId(layerId)
        if (safeGetLayer(m, hitId)) {
          m.setLayoutProperty(hitId, 'visibility', visible)
        }
      }
      for (const typeId of MAP_TRAIL_TYPE_IDS) {
        const visible = selection[typeId] ? 'visible' : 'none'
        for (const layerId of layerIdsForTrailToggle(typeId)) {
          setLayerVisibility(layerId, visible)
        }
        const aqpLayerId = AQP_LAYER_MAP[typeId]
        if (aqpLayerId) setLayerVisibility(aqpLayerId, visible)
      }
      return touched
    }

    const applyWithRetry = (attempt = 0) => {
      const count = applyVisibility()
      if (count === 0 && attempt < 12) {
        const t = setTimeout(() => applyWithRetry(attempt + 1), 80 * (attempt + 1))
        retryTimers.push(t)
      }
    }

    applyWithRetry()
    native.on('style.load', applyWithRetry)
    native.on('styledata', applyWithRetry)
    native.on('idle', applyWithRetry)

    return () => {
      for (const t of retryTimers) clearTimeout(t)
      retryTimers.length = 0
      native.off('style.load', applyWithRetry)
      native.off('styledata', applyWithRetry)
      native.off('idle', applyWithRetry)
    }
  }, [enabled, map, selectionKey, selection])

  return null
}
